import {
  Injectable,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
} from '@angular/core';
import { MissingRoofDataDialog } from '@shared/components/missing-roof-data-dialog/missing-roof-data-dialog';

export type MissingRoofDataChoice = 'draw' | 'rerun-detection' | 'cancelled';

/**
 * Explains a "no roof areas" planning/placement failure and lets the user
 * pick the actual fix — draw manually or re-run detection — instead of the
 * caller blindly retrying the planning stage into the same error again.
 */
@Injectable({
  providedIn: 'root',
})
export class MissingRoofDataDialogService {
  private componentRef?: ReturnType<typeof createComponent<MissingRoofDataDialog>>;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
  ) {}

  async confirm(): Promise<MissingRoofDataChoice> {
    if (!this.componentRef) {
      this.componentRef = createComponent(MissingRoofDataDialog, {
        environmentInjector: this.injector,
      });
      this.appRef.attachView(this.componentRef.hostView);
      document.body.appendChild(this.componentRef.location.nativeElement);
    }

    const instance = this.componentRef.instance;
    instance.open();

    return new Promise<MissingRoofDataChoice>((resolve) => {
      const drawSub = instance.drawManually.subscribe(() => {
        cleanup();
        resolve('draw');
      });
      const rerunSub = instance.rerunDetection.subscribe(() => {
        cleanup();
        resolve('rerun-detection');
      });
      const cancelSub = instance.cancelled.subscribe(() => {
        cleanup();
        resolve('cancelled');
      });

      const cleanup = () => {
        drawSub.unsubscribe();
        rerunSub.unsubscribe();
        cancelSub.unsubscribe();
      };
    });
  }
}
